import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { ImageUploadField } from "@/components/ImageUploadField";
import { SupplierRoomRatesSection, RoomRate } from "@/components/SupplierRoomRatesSection";
import { SupplierGuideRatesSection, GuideRate, GUIDE_RATE_CYCLES } from "@/components/SupplierGuideRatesSection";

type SupplierCategory = {
  id: string;
  name: string;
  parent_category_id: string | null;
};

type SupplierCategorySimple = {
  id: string;
  name: string;
};

type Supplier = {
  id: string;
  name: string;
  territory: string | null;
  city: string | null;
  category_id: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  description: string | null;
  product_name: string | null;
  hero_image_url: string | null;
  website?: string | null;
};

const supplierSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  territory: z.string().max(200).optional(),
  category_id: z.string().optional(),
  contact_name: z.string().max(200).optional(),
  email: z.string().email("Invalid email").max(255).optional().or(z.literal("")),
  cc_emails: z.array(z.string().email()).max(10).optional(),
  phone: z.string().max(50).optional(),
  address: z.string().max(500).optional(),
  website: z.string().max(500).optional(),
  notes: z.string().max(2000).optional(),
  description: z.string().max(2000, "Description must be less than 2000 characters").optional(),
  product_name: z.string().max(200).optional(),
  hero_image_url: z.string().min(1, "Hero image is required"),
  secondary_image_url: z.string().optional().or(z.literal("")),
  image_url_3: z.string().optional().or(z.literal("")),
  image_url_4: z.string().optional().or(z.literal("")),
});

interface AddSupplierDialogProps {
  categories: SupplierCategorySimple[];
  onSuccess: () => void;
  editSupplier?: Supplier | null;
  onClose?: () => void;
  // Controlled mode props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultCategoryId?: string;
  triggerless?: boolean;
  onSupplierCreated?: (supplierId: string) => void;
}

export function AddSupplierDialog({ 
  categories, 
  onSuccess, 
  editSupplier, 
  onClose,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultCategoryId,
  triggerless = false,
  onSupplierCreated,
}: AddSupplierDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Determine if we're in controlled or uncontrolled mode
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setInternalOpen;
  const [formData, setFormData] = useState({
    name: "",
    territory: "",
    city: "",
    category_id: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    notes: "",
    description: "",
    product_name: "",
    hero_image_url: "",
    secondary_image_url: "",
    image_url_3: "",
    image_url_4: "",
    cc_emails: [] as string[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [roomRates, setRoomRates] = useState<RoomRate[]>([]);
  const [guideRates, setGuideRates] = useState<GuideRate[]>([]);
  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState<string | null>(null);
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const addCcEmails = (raw: string) => {
    const parts = raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    setFormData((prev) => {
      const existing = new Set(prev.cc_emails.map((e) => e.toLowerCase()));
      const primary = prev.email.trim().toLowerCase();
      const invalid: string[] = [];
      const next = [...prev.cc_emails];
      for (const p of parts) {
        const lower = p.toLowerCase();
        if (!EMAIL_RE.test(p)) { invalid.push(p); continue; }
        if (lower === primary) { invalid.push(`${p} (same as primary email)`); continue; }
        if (existing.has(lower)) continue;
        if (next.length >= 10) { invalid.push(`${p} (max 10)`); continue; }
        existing.add(lower);
        next.push(p);
      }
      setCcError(invalid.length ? `Skipped: ${invalid.join(", ")}` : null);
      return { ...prev, cc_emails: next };
    });
    setCcInput("");
  };

  const removeCc = (email: string) => {
    setFormData((prev) => ({ ...prev, cc_emails: prev.cc_emails.filter((e) => e !== email) }));
    setCcError(null);
  };
  const queryClient = useQueryClient();

  const { data: countries } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch cities and parks for the selected country
  const selectedCountryId = countries?.find(c => c.name === formData.territory)?.id;
  const { data: supplierLocations } = useQuery({
    queryKey: ["supplier-locations", selectedCountryId],
    queryFn: async () => {
      if (!selectedCountryId) return [];
      const [citiesResult, parksResult] = await Promise.all([
        supabase.from("cities").select("id, name").eq("country_id", selectedCountryId).order("name"),
        supabase.from("parks").select("id, name").eq("country_id", selectedCountryId).order("name"),
      ]);
      return [
        ...(citiesResult.data || []).map(c => ({ ...c, type: 'city' as const })),
        ...(parksResult.data || []).map(p => ({ ...p, type: 'park' as const })),
      ].sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!selectedCountryId,
  });

  // Fetch full category data with parent relationships
  const { data: fullCategories } = useQuery({
    queryKey: ["supplier-categories-full"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("id, name, parent_category_id")
        .order("name");
      if (error) throw error;
      return data as SupplierCategory[];
    },
  });

  // Fetch existing room rates when editing
  const { data: existingRoomRates } = useQuery({
    queryKey: ["supplier-room-rates", editSupplier?.id],
    queryFn: async () => {
      if (!editSupplier?.id) return [];
      const { data, error } = await supabase
        .from("supplier_room_rates")
        .select("*")
        .eq("supplier_id", editSupplier.id)
        .eq("is_active", true)
        .order("room_type");
      if (error) throw error;
      return data;
    },
    enabled: !!editSupplier?.id,
  });

  // Determine if selected category is accommodation-related
  const isAccommodationCategory = useMemo(() => {
    if (!formData.category_id || !fullCategories) return false;
    
    const selectedCategory = fullCategories.find(c => c.id === formData.category_id);
    if (!selectedCategory) return false;
    
    // Check if the category name includes accommodation keywords
    const accommodationKeywords = ["accommodation", "hotel", "hostel", "guesthouse", "lodge", "resort", "camp", "villa", "apartment"];
    const categoryName = selectedCategory.name.toLowerCase();
    
    if (accommodationKeywords.some(keyword => categoryName.includes(keyword))) {
      return true;
    }
    
    // Check if parent category is accommodation
    if (selectedCategory.parent_category_id) {
      const parentCategory = fullCategories.find(c => c.id === selectedCategory.parent_category_id);
      if (parentCategory && accommodationKeywords.some(keyword => parentCategory.name.toLowerCase().includes(keyword))) {
        return true;
      }
    }
    
    return false;
  }, [formData.category_id, fullCategories]);

  // Villa-specific mode: simplified Private Hire entry
  const isVillaCategory = useMemo(() => {
    if (!formData.category_id || !fullCategories) return false;
    const selected = fullCategories.find(c => c.id === formData.category_id);
    return selected?.name?.toLowerCase() === "villas";
  }, [formData.category_id, fullCategories]);

  // Guide category detection
  const isGuideCategory = useMemo(() => {
    if (!formData.category_id || !fullCategories) return false;
    const selected = fullCategories.find(c => c.id === formData.category_id);
    const name = selected?.name?.toLowerCase() || "";
    return name === "guide" || name === "guides";
  }, [formData.category_id, fullCategories]);

  // Fetch existing guide rates when editing
  const { data: existingGuideRates } = useQuery({
    queryKey: ["supplier-guide-rates", editSupplier?.id],
    queryFn: async () => {
      if (!editSupplier?.id) return [];
      const { data, error } = await supabase
        .from("supplier_guide_rates" as any)
        .select("*")
        .eq("supplier_id", editSupplier.id)
        .eq("is_active", true);
      if (error) throw error;
      return data as any[];
    },
    enabled: !!editSupplier?.id,
  });

  useEffect(() => {
    if (existingGuideRates && existingGuideRates.length > 0) {
      setGuideRates(existingGuideRates.map((r: any) => ({
        id: r.id,
        service_type: r.service_type,
        service_mode: r.service_mode,
        rate: r.rate !== null && r.rate !== undefined ? Number(r.rate) : null,
        currency: r.currency || "ZAR",
        valid_from: r.valid_from,
        valid_to: r.valid_to,
        notes: r.notes || "",
        is_active: r.is_active,
      })));
    }
  }, [existingGuideRates]);

  useEffect(() => {
    if (editSupplier) {
      setFormData({
        name: editSupplier.name,
        territory: editSupplier.territory || "",
        city: editSupplier.city || "",
        category_id: editSupplier.category_id || "",
        contact_name: editSupplier.contact_name || "",
        email: editSupplier.email || "",
        phone: editSupplier.phone || "",
        address: editSupplier.address || "",
        website: (editSupplier as any).website || "",
        notes: editSupplier.notes || "",
        description: editSupplier.description || "",
        product_name: editSupplier.product_name || "",
        hero_image_url: editSupplier.hero_image_url || "",
        secondary_image_url: (editSupplier as any).secondary_image_url || "",
        image_url_3: (editSupplier as any).image_url_3 || "",
        image_url_4: (editSupplier as any).image_url_4 || "",
        cc_emails: Array.isArray((editSupplier as any).cc_emails)
          ? ((editSupplier as any).cc_emails as string[])
              .flatMap((e) => (typeof e === "string" ? e.split(/[,;]/) : []))
              .map((e) => e.trim())
              .filter((e) => EMAIL_RE.test(e))
          : [],
      });
      setOpen(true);
    }
  }, [editSupplier]);

  // Load existing room rates when editing
  useEffect(() => {
    if (existingRoomRates && existingRoomRates.length > 0) {
      setRoomRates(existingRoomRates.map(rate => ({
        id: rate.id,
        room_type: rate.room_type,
        description: rate.description || "",
        max_occupancy: rate.max_occupancy,
        number_of_rooms: (rate as any).number_of_rooms ?? null,
        single_rate: rate.single_rate ? Number(rate.single_rate) : null,
        double_rate: rate.double_rate ? Number(rate.double_rate) : null,
        triple_rate: rate.triple_rate ? Number(rate.triple_rate) : null,
        extra_person_rate: rate.extra_person_rate ? Number(rate.extra_person_rate) : null,
        currency: rate.currency,
        rate_basis: rate.rate_basis as "per_room" | "per_person",
        notes: rate.notes || "",
        is_active: rate.is_active,
      })));
    }
  }, [existingRoomRates]);

  // Seed a default Private Hire entry when switching to Villas with no existing rates
  useEffect(() => {
    if (isVillaCategory && roomRates.length === 0) {
      setRoomRates([{
        room_type: "PRIVATE HIRE",
        description: "",
          max_occupancy: 1,
        number_of_rooms: null,
        single_rate: null,
        double_rate: null,
        triple_rate: null,
        extra_person_rate: null,
        currency: "USD",
        rate_basis: "per_room",
        notes: "",
        is_active: true,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVillaCategory]);

  // Set default category when dialog opens in controlled mode
  useEffect(() => {
    if (open && defaultCategoryId && !editSupplier) {
      setFormData(prev => ({ ...prev, category_id: defaultCategoryId }));
    }
  }, [open, defaultCategoryId, editSupplier]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData): Promise<string | undefined> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error("Not authenticated");

      const primaryLower = (data.email || "").trim().toLowerCase();
      const cleanCc = (data.cc_emails || [])
        .map((e) => e.trim())
        .filter((e) => e.length > 0 && e.toLowerCase() !== primaryLower)
        .slice(0, 10);
      const supplierData = {
        ...data,
        cc_emails: cleanCc,
        category_id: data.category_id || null,
        user_id: session.user.id,
      };

      let supplierId: string;

      if (editSupplier) {
        const { error } = await supabase
          .from("suppliers")
          .update(supplierData)
          .eq("id", editSupplier.id);
        if (error) throw error;
        supplierId = editSupplier.id;
      } else {
        const { data: newSupplier, error } = await supabase
          .from("suppliers")
          .insert([supplierData])
          .select("id")
          .single();
        if (error) throw error;
        supplierId = newSupplier?.id;
      }

      // Handle room rates if this is an accommodation supplier
      if (isAccommodationCategory && supplierId) {
        // Villa-specific validation
        if (isVillaCategory) {
          for (const r of roomRates) {
            if (!r.room_type.trim()) continue;
            if (!r.number_of_rooms || r.number_of_rooms < 1) {
              throw new Error("Each villa must have a Number of Rooms (1 or more).");
            }
            if (!r.max_occupancy || r.max_occupancy < 1) {
              throw new Error("Each villa must have Maximum Guests (1 or more).");
            }
            if (!r.single_rate || r.single_rate <= 0) {
              throw new Error("Each villa must have a Flat Private Rate greater than 0.");
            }
          }
        }
        // Separate rates into existing (have id) vs new (no id)
        const existingRates = roomRates.filter(r => r.id && r.room_type.trim() !== "");
        const newRates = roomRates.filter(r => !r.id && r.room_type.trim() !== "");
        let keptIds = existingRates.map(r => r.id!);

        // Update existing rates in-place (preserves foreign key references)
        for (const rate of existingRates) {
          const { error: updateError } = await supabase
            .from("supplier_room_rates")
            .update({
              room_type: rate.room_type,
              description: rate.description || null,
              max_occupancy: rate.max_occupancy,
              number_of_rooms: rate.number_of_rooms ?? null,
              single_rate: rate.single_rate,
              double_rate: isVillaCategory ? null : rate.double_rate,
              triple_rate: isVillaCategory ? null : rate.triple_rate,
              extra_person_rate: isVillaCategory ? null : rate.extra_person_rate,
              currency: rate.currency,
              rate_basis: isVillaCategory ? "per_room" : rate.rate_basis,
              notes: rate.notes || null,
              is_active: rate.is_active,
            } as any)
            .eq("id", rate.id!);
          if (updateError) throw updateError;
        }

        // Insert new rates
        if (newRates.length > 0) {
          const ratesToInsert = newRates.map(rate => ({
            supplier_id: supplierId,
            room_type: rate.room_type,
            description: rate.description || null,
            max_occupancy: rate.max_occupancy,
            number_of_rooms: rate.number_of_rooms ?? null,
            single_rate: rate.single_rate,
            double_rate: isVillaCategory ? null : rate.double_rate,
            triple_rate: isVillaCategory ? null : rate.triple_rate,
            extra_person_rate: isVillaCategory ? null : rate.extra_person_rate,
            currency: rate.currency,
            rate_basis: isVillaCategory ? "per_room" : rate.rate_basis,
            notes: rate.notes || null,
            is_active: rate.is_active,
          }));
          const { data: insertedRates, error: insertError } = await supabase
            .from("supplier_room_rates")
            .insert(ratesToInsert as any)
            .select("id");
          if (insertError) throw insertError;
          if (insertedRates) {
            keptIds.push(...insertedRates.map(r => r.id));
          }
        }

        // Soft-delete removed rates (safe even if referenced by itinerary_items)
        if (keptIds.length > 0) {
          const { error: softDeleteError } = await supabase
            .from("supplier_room_rates")
            .update({ is_active: false })
            .eq("supplier_id", supplierId)
            .not("id", "in", `(${keptIds.join(",")})`);
          if (softDeleteError) throw softDeleteError;
        } else {
          // User removed all rates — soft-delete everything for this supplier
          const { error: softDeleteAllError } = await supabase
            .from("supplier_room_rates")
            .update({ is_active: false })
            .eq("supplier_id", supplierId);
          if (softDeleteAllError) throw softDeleteAllError;
        }
      }

      // Handle guide rates if this is a Guide supplier
      if (isGuideCategory && supplierId) {
        const validRows = guideRates.filter((r) => r.rate !== null && r.rate !== undefined && r.rate >= 0);
        const existingRows = validRows.filter((r) => r.id);
        const newRows = validRows.filter((r) => !r.id);
        const keptIds: string[] = existingRows.map((r) => r.id!);

        for (const r of existingRows) {
          const { error: upErr } = await supabase
            .from("supplier_guide_rates" as any)
            .update({
              service_type: r.service_type,
              service_mode: r.service_mode,
              rate: r.rate,
              currency: r.currency,
              valid_from: r.valid_from,
              valid_to: r.valid_to,
              notes: r.notes || null,
              is_active: true,
            } as any)
            .eq("id", r.id!);
          if (upErr) throw upErr;
        }

        if (newRows.length > 0) {
          const toInsert = newRows.map((r) => ({
            supplier_id: supplierId,
            service_type: r.service_type,
            service_mode: r.service_mode,
            rate: r.rate,
            currency: r.currency,
            valid_from: r.valid_from,
            valid_to: r.valid_to,
            notes: r.notes || null,
            is_active: true,
          }));
          const { data: inserted, error: insErr } = await supabase
            .from("supplier_guide_rates" as any)
            .insert(toInsert as any)
            .select("id");
          if (insErr) throw insErr;
          if (inserted) keptIds.push(...inserted.map((r: any) => r.id));
        }

        // Soft-delete rows removed by the user (blanked out)
        if (keptIds.length > 0) {
          const { error: delErr } = await supabase
            .from("supplier_guide_rates" as any)
            .update({ is_active: false } as any)
            .eq("supplier_id", supplierId)
            .not("id", "in", `(${keptIds.join(",")})`);
          if (delErr) throw delErr;
        } else {
          const { error: delAllErr } = await supabase
            .from("supplier_guide_rates" as any)
            .update({ is_active: false } as any)
            .eq("supplier_id", supplierId);
          if (delAllErr) throw delAllErr;
        }
      }

      return supplierId;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-room-rates"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-guide-rates"] });
      toast.success(editSupplier ? "Supplier updated successfully" : "Supplier created successfully");
      setOpen(false);
      setFormData({
        name: "",
        territory: "",
        city: "",
        category_id: "",
        contact_name: "",
        email: "",
        phone: "",
        address: "",
        website: "",
        notes: "",
        description: "",
        product_name: "",
        hero_image_url: "",
        secondary_image_url: "",
        image_url_3: "",
        image_url_4: "",
        cc_emails: [],
      });
      setRoomRates([]);
      setGuideRates([]);
      setErrors({});
      onSuccess();
      if (onClose) onClose();
      if (onSupplierCreated && data) onSupplierCreated(data);
    },
    onError: (error) => {
      toast.error(editSupplier ? "Failed to update supplier" : "Failed to add supplier", {
        description: (error as Error)?.message ?? "Unknown error",
      });
      console.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = supplierSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          newErrors[err.path[0].toString()] = err.message;
        }
      });
      setErrors(newErrors);
      const firstErr = result.error.errors[0];
      const firstPath = firstErr?.path[0]?.toString();
      toast.error("Please fix the highlighted fields", {
        description: firstErr ? `${firstPath ? firstPath + ": " : ""}${firstErr.message}` : undefined,
      });
      if (firstPath) {
        setTimeout(() => {
          document.getElementById(firstPath)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 0);
      }
      return;
    }

    setErrors({});
    createMutation.mutate(formData);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: "",
      territory: "",
      city: "",
      category_id: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      website: "",
      notes: "",
      description: "",
      product_name: "",
      hero_image_url: "",
      secondary_image_url: "",
      image_url_3: "",
      image_url_4: "",
        cc_emails: [],
    });
    setRoomRates([]);
    setGuideRates([]);
    setErrors({});
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) handleClose();
    }}>
      {!triggerless && (
        <DialogTrigger asChild>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Create Supplier
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editSupplier ? "Edit Supplier" : "Create New Supplier"}</DialogTitle>
          <DialogDescription>
            {editSupplier ? "Update supplier information" : "Create a new supplier in your database"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <ImageUploadField
            value={formData.hero_image_url}
            onChange={(url) => setFormData({ ...formData, hero_image_url: url || "" })}
            entityType="supplier"
            required
            showMissingWarning={!!editSupplier && !editSupplier.hero_image_url}
          />
          {errors.hero_image_url && <p className="text-sm text-destructive">{errors.hero_image_url}</p>}

          <div className="space-y-2">
            <ImageUploadField
              value={formData.secondary_image_url || null}
              onChange={(url) => setFormData({ ...formData, secondary_image_url: url || "" })}
              entityType="supplier"
            />
            <p className="text-xs text-muted-foreground">Secondary Image (Optional)</p>
          </div>
          <div className="space-y-2">
            <ImageUploadField
              value={formData.image_url_3 || null}
              onChange={(url) => setFormData({ ...formData, image_url_3: url || "" })}
              entityType="supplier"
            />
            <p className="text-xs text-muted-foreground">Image 3 (Optional)</p>
          </div>
          <div className="space-y-2">
            <ImageUploadField
              value={formData.image_url_4 || null}
              onChange={(url) => setFormData({ ...formData, image_url_4: url || "" })}
              entityType="supplier"
            />
            <p className="text-xs text-muted-foreground">Image 4 (Optional)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Supplier Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Safari Lodge Inc."
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="territory">Country</Label>
              <Select
                value={formData.territory}
                onValueChange={(value) => setFormData({ ...formData, territory: value, city: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries?.map((country) => (
                    <SelectItem key={country.id} value={country.name}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City / Park</Label>
              <Select
                value={formData.city}
                onValueChange={(value) => setFormData({ ...formData, city: value })}
                disabled={!formData.territory}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.territory ? "Select city/park" : "Select country first"} />
                </SelectTrigger>
                <SelectContent>
                  {supplierLocations?.map((loc) => (
                    <SelectItem key={loc.id} value={loc.name}>
                      {loc.name} ({loc.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={formData.category_id}
              onValueChange={(value) => setFormData({ ...formData, category_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Show product name field only when TOUR category is selected */}
          {categories.find(cat => cat.id === formData.category_id)?.name.toUpperCase() === "TOUR" && (
            <div className="space-y-2">
              <Label htmlFor="product_name">Product Name</Label>
              <Input
                id="product_name"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                placeholder="e.g., Gorilla Trekking Experience"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="e.g., John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="e.g., contact@supplier.com"
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc_emails">CC Emails (for booking requests)</Label>
            {formData.cc_emails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.cc_emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeCc(email)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${email}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                id="cc_emails"
                type="email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addCcEmails(ccInput);
                  }
                }}
                onBlur={() => { if (ccInput.trim()) addCcEmails(ccInput); }}
                placeholder="e.g., reservations@supplier.com"
                disabled={formData.cc_emails.length >= 10}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addCcEmails(ccInput)}
                disabled={!ccInput.trim() || formData.cc_emails.length >= 10}
              >
                Add
              </Button>
            </div>
            {ccError && <p className="text-xs text-destructive">{ccError}</p>}
            <p className="text-xs text-muted-foreground">
              Add one email at a time. These addresses are CC'd on every booking request for this supplier. (Max 10)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g., +27 11 123 4567"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter physical address"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="e.g., https://supplier.com"
            />
            <p className="text-xs text-muted-foreground">
              Shown on itinerary cards so guests and partners can visit the supplier's website.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the property or service for client-facing documents"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">Appears on client quotes</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Private notes not visible to clients"
              rows={3}
            />
          </div>

          {/* Room Rates Section - Only show for accommodation categories */}
          {isAccommodationCategory && (
            <>
              <Separator className="my-6" />
              <SupplierRoomRatesSection
                roomRates={roomRates}
                onChange={setRoomRates}
                defaultCurrency="USD"
                isVilla={isVillaCategory}
              />
            </>
          )}

          {/* Guide Rates Section - Only show for Guide categories */}
          {isGuideCategory && (
            <>
              <Separator className="my-6" />
              <SupplierGuideRatesSection
                rates={guideRates}
                onChange={setGuideRates}
              />
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || !formData.hero_image_url}>
              {createMutation.isPending ? "Saving..." : editSupplier ? "Update Supplier" : "Create Supplier"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
